<template>
    <component
        :is="actionType === 'link' ? 'RouterLink' : 'button'"
        :id="id"
        :type="actionType === 'action' ? type : undefined"
        :class="[
            'action-button',
            isRounded && 'action-button-rounded',
            isFullWidth && 'action-button-full',
            isMobileFullWidth && 'action-button-mobile-full',
            buttonStyleClass,
            buttonSizeClass,
            gapClass,
        ]"
        v-bind="{
            ...componentProps,
            ...$attrs,
            ...(actionType === 'action' ? { onClick: emitClick } : {}),
        }"
        :disabled="(actionType === 'action' && isLoading) || disabled"
    >
        <template v-if="isLoading">
            <i class="fas fa-spinner fa-spin action-button-icon"></i>
            <span class="action-button-text">
                {{ loadingText }}
            </span>
        </template>

        <template v-else>
            <i
                v-if="icon && iconPosition === 'left'"
                :class="['action-button-icon', icon, centerIconIsNeeded && 'action-button-center-icon']"
            ></i>

            <span class="action-button-text">
                {{ text }}
            </span>

            <i
                v-if="icon && iconPosition === 'right'"
                :class="['action-button-icon', icon, centerIconIsNeeded && 'action-button-center-icon']"
            ></i>
        </template>
    </component>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { RouterLink } from "vue-router";

import type { ButtonActionType, ButtonStyleType, ButtonSize } from "@/types/buttons";
import type { IconPosition } from "@/types/icons";

export default defineComponent({
    components: {
        RouterLink,
    },
    name: "ActionButton",
    inheritAttrs: false,

    emits: ["click"],
    props: {
        actionType: {
            type: String as PropType<ButtonActionType>,
            default: "action",
        },
        styleType: {
            type: String as PropType<ButtonStyleType>,
            default: "neutral-outlined",
        },
        type: {
            type: String as PropType<"button" | "submit" | "reset">,
            default: "button",
        },
        text: {
            type: String,
            default: "Button text",
        },
        size: {
            type: String as PropType<ButtonSize>,
            default: "lg",
        },
        icon: {
            type: String,
            default: "fas fa-question",
        },
        iconPosition: {
            type: String as PropType<IconPosition>,
            default: "none",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        to: {
            type: String,
            default: "",
        },
        isExternal: {
            type: Boolean,
            default: false,
        },
        isRounded: {
            type: Boolean,
            default: false,
        },
        isFullWidth: {
            type: Boolean,
            default: false,
        },
        isMobileFullWidth: {
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        loadingText: {
            type: String,
            default: "Processing...",
        },
        centerIconIsNeeded: {
            type: Boolean,
            default: false,
        },
        id: String,
    },

    computed: {
        buttonStyleClass(): string {
            switch (this.styleType) {
                case "primary-brand-filled":
                    return "style-primary-brand-filled";
                case "primary-brand-soft":
                    return "style-primary-brand-soft";
                case "primary-brand-transparent":
                    return "style-primary-brand-transparent";
                case "secondary-brand-filled":
                    return "style-secondary-brand-filled";
                case "neutral-filled":
                    return "style-neutral-filled";
                case "neutral-outlined":
                    return "style-neutral-outlined";
                case "neutral-transparent":
                    return "style-neutral-transparent";
                case "neutral-transparent-subtle":
                    return "style-neutral-transparent-subtle";
                case "delete-filled":
                    return "style-delete-filled";
                case "delete-soft":
                    return "style-delete-soft";
                case "delete-outlined":
                    return "style-delete-outlined";
                case "delete-transparent":
                    return "style-delete-transparent";
                default:
                    return "style-neutral-outlined";
            }
        },

        buttonSizeClass(): string {
            switch (this.size) {
                case "xs":
                    return "size-xs";
                case "sm":
                    return "size-sm";
                case "md":
                    return "size-md";
                case "lg":
                    return "size-lg";
                case "xl":
                    return "size-xl";
                case "xxl":
                    return "size-xxl";
                default:
                    return "size-lg";
            }
        },

        gapClass(): string {
            switch (this.size) {
                case "xs":
                    return "gap-xs";
                case "sm":
                    return "gap-sm";
                case "md":
                    return "gap-md";
                case "lg":
                    return "gap-lg";
                case "xl":
                    return "gap-xl";
                case "xxl":
                    return "gap-xxl";
                default:
                    return "gap-lg";
            }
        },

        componentProps(): Record<string, any> {
            if (this.actionType === "link") {
                return {
                    to: this.to,
                    target: this.isExternal ? "_blank" : "_self",
                    rel: this.isExternal ? "noopener noreferrer" : undefined,
                };
            }
            return {};
        },
    },

    methods: {
        emitClick: function () {
            if (!this.disabled) {
                this.$emit("click");
            }
        },
    },
});
</script>

<style scoped>
@import "@/style/components/buttons.css";
</style>
